import { lazy, Suspense } from "react";

import { lazyWithBackgroundWarm } from "@client/utils/backgroundChunkWarming";
import { isHtmlContent } from "@lib/noteContentFormat";
import { stripMarkdownSyntax } from "@lib/noteText";

import type {
  MarkdownComponents,
  MarkdownRehypePlugins,
  RehypeSanitizeSchema,
} from "./MarkdownViewerImpl";

// This module is the public face of note/summary rendering and is imported
// all over the EAGER surface (the funder workspace, pyramids, general notes).
// The actual rendering machinery — react-markdown, the remark/rehype
// pipelines, parse5 — is several hundred KB of startup parse, so it lives in
// `./MarkdownViewerImpl`, loaded through lazy() below: only the pure string
// utils here ride the entry bundle. While the impl chunk loads (first render
// only; the background chunk warm usually has it cached within seconds of
// startup) the components fall back to the note's plain text, so content is
// readable immediately and upgrades in place to formatted output.

/**
 * Note/summary content in this app comes in two shapes:
 *
 *  - **Markdown / plain text** — LLM output, legacy notes, anything authored as
 *    prose. Rendered with `MarkdownViewer`.
 *  - **HTML** — produced by the Lexical rich-text editor (see `RichTextEditor`),
 *    e.g. `<p dir="ltr"><span style="white-space: pre-wrap;">…</span></p>`.
 *    Rendered with `HtmlContentViewer`.
 *
 * Use {@link NotesContentViewer} as the single entry point: it detects which
 * shape the value is and dispatches to the correct renderer. Always prefer it
 * over calling a markdown renderer directly on note content.
 *
 * Why the two paths must stay separate: a CommonMark markdown parser is NOT a
 * safe way to render HTML. Its block rules (indented code blocks, blank-line
 * termination of HTML blocks, etc.) reinterpret real editor HTML and either
 * escape the tags as literal text (you see `<p class="…"><span …>` on screen) or
 * silently drop/relocate content. Feeding Lexical HTML through `react-markdown`
 * is exactly the bug this module exists to prevent.
 *
 * Detection itself lives in `@lib/noteContentFormat` (re-exported here) so
 * server-side consumers share the exact same classification.
 */
export { isHtmlContent };

/**
 * Project note content to PLAIN TEXT — the counterpart to the rendering
 * projection ({@link NotesContentViewer}). Use this for consumers that *process*
 * a note rather than display it richly: quote matching, excerpts/previews, search
 * indexing. Those operate on the note string directly, and if the note is Lexical
 * HTML they otherwise see raw tags (e.g. a quote-context excerpt sliced mid-tag
 * shows `</span></p><p …>` to the user). Markdown / plain text passes through
 * unchanged.
 *
 * Deterministic string ops, intentionally NOT a DOM/markdown parser: this is the
 * one place we discard formatting rather than render it, and it must behave
 * identically in the browser and in tests (where `DOMParser` is absent). It's
 * safe for editor HTML specifically because Lexical's serializer escapes text
 * content (a literal `<`/`>`/`&` in a note becomes an entity), so stripping tags
 * can't swallow real text. Block boundaries (`</p>`, `</li>`, `<br>`) become
 * newlines so adjacent paragraphs don't fuse ("…of it.NEGATIVES:").
 */
export function htmlToPlainText(value: string): string {
  if (!isHtmlContent(value)) {
    return value;
  }

  const withBreaks = value
    .replace(/<\/(p|li|ul|ol|div|blockquote|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n");
  const stripped = withBreaks.replace(/<[^>]+>/g, "");
  const decoded = stripped
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&#x0*27;/gi, "'")
    // `&amp;` last so `&amp;lt;` decodes to `&lt;`, not `<`.
    .replace(/&amp;/gi, "&");
  return decoded
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
}

const loadImpl = () => import("./MarkdownViewerImpl");
// One registration warms the shared impl chunk; the sibling lazy() calls below
// resolve from the module cache the moment it lands.
const LazyMarkdownViewer = lazyWithBackgroundWarm(async () => ({
  default: (await loadImpl()).MarkdownViewerImpl,
}));
const LazyHtmlContentViewer = lazy(async () => ({
  default: (await loadImpl()).HtmlContentViewerImpl,
}));

// Tag-shaped tokens in MARKDOWN-path fallback text: a name, optional
// attributes, closing/self-closing forms. Deliberately narrower than
// htmlToPlainText's strip — `<3` and `<https://…>` autolinks don't match
// (no attribute whitespace after the "name", and `:` breaks the name), so
// legitimate markdown text survives the fallback.
const TAG_SHAPED_TOKEN = /<\/?[a-zA-Z][a-zA-Z0-9-]*(\s[^>]*)?\/?>/g;

/**
 * The text the fallback stand-in shows: the note's plain text (formatting
 * stripped, structure preserved as line breaks).
 *
 * Markdown-path values ALSO get tag-shaped tokens stripped and injected HTML
 * entities decoded — not just Lexical-shaped HTML — because callers can
 * inject markup into markdown before rendering (`CitationMarkdownViewer`
 * preprocesses citations into `<sup>`/`<q data-…>` tags with escapeHtml'd
 * bodies; pyramid tiers bold an escapeHtml'd prefix and append an `&rsaquo;`
 * expand marker) and those must not flash as literal tag soup or `&amp;`
 * text. The real pipeline drops unknown raw tags via rehype-sanitize and
 * decodes entity references, so this tracks the rendered output. (Tags are
 * stripped BEFORE decoding, so a decoded literal `<` can't be mistaken for
 * markup.)
 */
export function fallbackPlainText(value: string): string {
  if (isHtmlContent(value)) {
    return htmlToPlainText(value);
  }

  // Tag-shaped tokens strip FIRST (escapeHtml'd injected bodies survive —
  // they hold no literal tags until decoding), then the markdown projection
  // strips formatting markers and decodes entity refs (`&rsaquo;` included)
  // — so the stand-in shows visible text, not `**`/`-` syntax.
  return stripMarkdownSyntax(value.replace(TAG_SHAPED_TOKEN, ""));
}

/**
 * Readable stand-in while the rendering pipeline chunk loads. Content is
 * visible immediately and upgrades in place — never a blank region.
 */
function PlainTextFallback({ value }: { value: string }) {
  const text = fallbackPlainText(value);
  // data-passage-exclude: this text is a transient stand-in whose offsets
  // differ from the real rendered prose, so passage anchors must never be
  // captured against it (a deep link copied here could not land once the
  // real renderer mounts) nor matched against it on landing.
  return (
    <div
      className="whitespace-pre-wrap"
      data-markdown-fallback=""
      data-passage-exclude=""
    >
      {text}
    </div>
  );
}

export function MarkdownViewer({
  value,
  components,
  rehypeSchema,
  extraRehypePlugins,
  fallbackValue,
}: {
  value: string;
  components?: MarkdownComponents;
  rehypeSchema?: RehypeSanitizeSchema;
  /** Extra rehype plugins run before sanitize (e.g. citation-token expansion). */
  extraRehypePlugins?: MarkdownRehypePlugins;
  /** Source-shaped value for the Suspense stand-in when `value` contains
   * parser-only tokens that should never be user-visible. */
  fallbackValue?: string;
}) {
  return (
    <Suspense fallback={<PlainTextFallback value={fallbackValue ?? value} />}>
      <LazyMarkdownViewer
        value={value}
        components={components}
        rehypeSchema={rehypeSchema}
        extraRehypePlugins={extraRehypePlugins}
      />
    </Suspense>
  );
}

/**
 * Renders pre-sanitized-as-HTML note content. Prefer {@link NotesContentViewer}
 * unless you already know the value is HTML and never markdown.
 */
export function HtmlContentViewer({
  value,
  components,
  rehypeSchema,
}: {
  value: string;
  components?: MarkdownComponents;
  rehypeSchema?: RehypeSanitizeSchema;
}) {
  return (
    <Suspense fallback={<PlainTextFallback value={value} />}>
      <LazyHtmlContentViewer
        value={value}
        components={components}
        rehypeSchema={rehypeSchema}
      />
    </Suspense>
  );
}

/**
 * Canonical renderer for note/summary content. Detects whether the value is
 * HTML (Lexical editor output) or markdown/plain text and renders it with the
 * matching pipeline. Use this everywhere notes are displayed so HTML notes
 * never get run through the markdown parser (which corrupts them).
 *
 * `contentType` overrides auto-detection. Pass it when the `value` has had
 * markup injected before rendering (e.g. fact-check highlighting wraps matches
 * in `<mark>`), so a markdown note whose first highlight lands at offset 0 is
 * not misread as HTML. Classify against the *original* note, not the injected
 * markup.
 */
/**
 * Note-content typography: the list markers/indentation and paragraph
 * spacing Lexical-authored notes have always rendered with. Applied by BOTH
 * NotesContentViewer branches so a note converted from HTML to markdown
 * keeps identical list rendering (Tailwind preflight otherwise strips
 * markers on surfaces that only style paragraphs/links).
 */
export const NOTE_CONTENT_CLASSES =
  "[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_p]:mb-2 [&_p:last-child]:mb-0";

/**
 * Lexical-HTML only: Lexical nests a sublist inside a marker-less WRAPPER
 * `<li>` whose sole content is the list, and that wrapper must not show a
 * bullet. Markdown rendering nests the sublist inside the REAL parent item
 * (`- parent` / `  - child`), so applying this there would erase the
 * parent's marker — keep it off the markdown branch.
 */
export const LEXICAL_WRAPPER_LI_CLASSES =
  "[&_li:has(>ul)]:list-none [&_li:has(>ol)]:list-none";

export function NotesContentViewer({
  value,
  components,
  rehypeSchema,
  contentType,
}: {
  value: string;
  components?: MarkdownComponents;
  rehypeSchema?: RehypeSanitizeSchema;
  contentType?: "html" | "markdown";
}) {
  const isHtml =
    contentType != null ? contentType === "html" : isHtmlContent(value);

  if (isHtml) {
    return (
      <HtmlContentViewer
        value={value}
        components={components}
        rehypeSchema={rehypeSchema}
      />
    );
  }

  return (
    <div className={NOTE_CONTENT_CLASSES}>
      <MarkdownViewer
        value={value}
        components={components}
        rehypeSchema={rehypeSchema}
      />
    </div>
  );
}
